import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TablePagination from '@mui/material/TablePagination';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { EVENT_TYPE_LABEL_HU, EventType } from '@persped/shared';
import { useState } from 'react';
import { useEvents } from '../../api/queries';
import { EventTable } from '../../components/EventTable';
import { ProblemAlert } from '../../components/ProblemAlert';

/**
 * Globális eseménynapló (spec 12. §).
 *
 * Ez a kereszt-entitású, audit célú nézet — a mappa- és raklapelőzménnyel
 * szemben nem egy entitáshoz kötött. Az Androidon nincs megfelelője, ezért
 * ma ez az egyetlen felület, amin a teljes napló átnézhető.
 *
 * A típusválasztó a magyar címkék szerint van rendezve, mert a 26 nyers
 * enumérték ábécésorrendje semmit nem mond a felhasználónak.
 */
const EVENT_TYPES = Object.values(EventType).sort((a, b) =>
  EVENT_TYPE_LABEL_HU[a].localeCompare(EVENT_TYPE_LABEL_HU[b], 'hu'),
);

export function EventLogPage() {
  const [eventType, setEventType] = useState('');
  const [folderBarcode, setFolderBarcode] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  // A szerver `MAP-\d{5}` formátumot vár; részleges beírásnál ne küldjük el,
  // különben minden leütés egy 400-as választ generálna.
  const folderFilter = /^MAP-\d{5}$/.test(folderBarcode) ? folderBarcode : undefined;

  const events = useEvents({
    eventType: eventType || undefined,
    folderBarcode: folderFilter,
    page: page + 1,
    pageSize,
  });

  return (
    <Stack spacing={2}>
      <Typography variant="h5" component="h2">
        Eseménynapló
      </Typography>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
          <TextField
            select
            label="Eseménytípus"
            value={eventType}
            onChange={(e) => {
              setEventType(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 280 }}
          >
            <MenuItem value="">Mind</MenuItem>
            {EVENT_TYPES.map((t) => (
              <MenuItem key={t} value={t}>
                {EVENT_TYPE_LABEL_HU[t]}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Mappa vonalkód"
            value={folderBarcode}
            onChange={(e) => {
              setFolderBarcode(e.target.value.toUpperCase());
              setPage(0);
            }}
            placeholder="MAP-00001"
            helperText={
              folderBarcode.length > 0 && !folderFilter ? 'Teljes vonalkód kell a szűréshez.' : ' '
            }
            slotProps={{ htmlInput: { style: { fontFamily: 'monospace' } } }}
            sx={{ minWidth: 200 }}
          />
        </Stack>
      </Paper>

      {events.isError && <ProblemAlert error={events.error} />}

      {events.isPending ? (
        <Stack sx={{ py: 6, alignItems: 'center' }}>
          <CircularProgress />
        </Stack>
      ) : events.data ? (
        <Paper variant="outlined">
          <EventTable events={events.data.items} emptyText="Nincs a szűrésnek megfelelő esemény." />
          <TablePagination
            component="div"
            count={events.data.total}
            page={page}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[50, 100, 200]}
            labelRowsPerPage="Sorok oldalanként:"
          />
        </Paper>
      ) : null}
    </Stack>
  );
}
